import { cn } from '@/lib/utils'

export type IconName =
  | 'calculator'
  | 'clock'
  | 'download'
  | 'image'
  | 'file'
  | 'menu'
  | 'search'
  | 'arrow'
  | 'chevron'
  | 'plus'
  | 'minus'
  | 'alert'
  | 'bulb'
  | 'tag'
  | 'trend'
  | 'scale'
  | 'target'
  | 'wallet'
  | 'card'
  | 'calendar'
  | 'user'
  | 'send'
  | 'whatsapp'
  | 'x'
  | 'star'
  | 'check'
  | 'eye-off'

interface IconProps {
  name: IconName
  size?: number
  strokeWidth?: number
  className?: string
}

/**
 * Ícones do design system (stroke 1.7, cantos redondos no traço).
 * A COR vem do `currentColor` — defina via className (ex.: text-brand).
 */
export function Icon({ name, size = 18, strokeWidth = 1.7, className }: IconProps) {
  const stroke = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className: cn('shrink-0', className),
  }

  switch (name) {
    case 'calculator':
      return (
        <svg {...stroke}>
          <rect x="4" y="2" width="16" height="20" />
          <line x1="8" y1="6" x2="16" y2="6" />
          <line x1="8" y1="10" x2="8" y2="10" />
          <line x1="12" y1="10" x2="12" y2="10" />
          <line x1="16" y1="10" x2="16" y2="10" />
          <line x1="8" y1="14" x2="8" y2="14" />
          <line x1="12" y1="14" x2="12" y2="14" />
          <line x1="16" y1="14" x2="16" y2="18" />
          <line x1="8" y1="18" x2="12" y2="18" />
        </svg>
      )
    case 'clock':
      return (<svg {...stroke}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>)
    case 'download':
      return (<svg {...stroke}><path d="M12 3v12" /><path d="M7 11l5 5 5-5" /><path d="M4 21h16" /></svg>)
    case 'image':
      return (<svg {...stroke}><rect x="3" y="4" width="18" height="16" /><circle cx="8.5" cy="9.5" r="1.5" /><path d="M21 16l-5-5L5 20" /></svg>)
    case 'file':
      return (<svg {...stroke}><path d="M14 2H6v20h12V6z" /><path d="M14 2v4h4" /><line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="13" y2="17" /></svg>)
    case 'menu':
      return (<svg {...stroke}><line x1="4" y1="7" x2="20" y2="7" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="17" x2="20" y2="17" /></svg>)
    case 'search':
      return (<svg {...stroke}><circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" /></svg>)
    case 'arrow':
      return (<svg {...stroke}><line x1="5" y1="12" x2="19" y2="12" /><path d="M13 6l6 6-6 6" /></svg>)
    case 'chevron':
      return (<svg {...stroke}><path d="M9 6l6 6-6 6" /></svg>)
    case 'plus':
      return (<svg {...stroke}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>)
    case 'minus':
      return (<svg {...stroke}><line x1="5" y1="12" x2="19" y2="12" /></svg>)
    case 'alert':
      return (<svg {...stroke}><path d="M12 3l9 16H3z" /><line x1="12" y1="10" x2="12" y2="14" /><line x1="12" y1="17" x2="12" y2="17" /></svg>)
    case 'bulb':
      return (<svg {...stroke}><path d="M9 18h6" /><path d="M10 21h4" /><path d="M12 3a6 6 0 0 0-4 10.5c.8.8 1 1.5 1 2.5h6c0-1 .2-1.7 1-2.5A6 6 0 0 0 12 3z" /></svg>)
    case 'tag':
      return (<svg {...stroke}><path d="M3 12V5a2 2 0 0 1 2-2h7l9 9-9 9-9-9z" /><circle cx="7.5" cy="7.5" r="1.2" /></svg>)
    case 'trend':
      return (<svg {...stroke}><path d="M3 17l5-5 4 4 8-8" /><path d="M16 8h4v4" /></svg>)
    case 'scale':
      return (<svg {...stroke}><line x1="12" y1="3" x2="12" y2="21" /><path d="M5 7h14" /><path d="M5 7l-2 5h4z" /><path d="M19 7l-2 5h4z" /></svg>)
    case 'target':
      return (<svg {...stroke}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></svg>)
    case 'wallet':
      return (<svg {...stroke}><rect x="3" y="6" width="18" height="14" /><path d="M3 10h18" /><circle cx="17" cy="14" r="1" /></svg>)
    case 'card':
      return (<svg {...stroke}><rect x="3" y="5" width="18" height="14" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="7" y1="15" x2="11" y2="15" /></svg>)
    case 'calendar':
      return (<svg {...stroke}><rect x="3" y="4" width="18" height="18" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="16" y1="2" x2="16" y2="6" /></svg>)
    case 'user':
      return (<svg {...stroke}><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></svg>)
    case 'send':
      return (<svg {...stroke}><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></svg>)
    case 'whatsapp':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={cn('shrink-0', className)}>
          <path d="M17.5 14.4c-.3-.15-1.7-.85-2-.95-.25-.1-.45-.15-.65.15-.2.3-.75.95-.9 1.15-.15.2-.35.2-.65.05-1.75-.85-2.9-1.55-4.05-3.5-.3-.55.3-.5.85-1.65.1-.2.05-.35-.05-.5-.1-.15-.65-1.55-.9-2.15-.25-.55-.5-.5-.65-.5h-.55c-.2 0-.5.05-.75.35-.25.3-1 1-1 2.4 0 1.45 1.05 2.85 1.2 3.05.15.2 2.05 3.15 5 4.4 1.85.8 2.6.85 3.5.7.55-.05 1.7-.7 1.95-1.35.25-.65.25-1.25.15-1.35-.05-.15-.25-.2-.55-.35M12 21.5a9.5 9.5 0 0 1-4.85-1.35l-3.4.9.9-3.3A9.5 9.5 0 1 1 12 21.5m0-21a11.5 11.5 0 0 0-9.9 17.35L.5 23.5l5.8-1.5A11.5 11.5 0 1 0 12 .5" />
        </svg>
      )
    case 'x':
      return (<svg {...stroke}><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>)
    case 'star':
      return (<svg {...stroke}><path d="M12 3l2.6 5.6 6.4.8-4.7 4.4 1.2 6.2L12 17.7 6.5 20l1.2-6.2L3 9.4l6.4-.8z" /></svg>)
    case 'check':
      return (<svg {...stroke}><path d="M4 12l5 5L20 6" /></svg>)
    case 'eye-off':
      return (<svg {...stroke}><path d="M3 3l18 18" /><path d="M10.6 10.6a2.5 2.5 0 0 0 3.4 3.4" /><path d="M9.4 5.2A9 9 0 0 1 12 5c6 0 10 7 10 7a17 17 0 0 1-2.5 3.2M6.5 6.5A17 17 0 0 0 2 12s4 7 10 7a9 9 0 0 0 3.3-.6" /></svg>)
    default:
      return null
  }
}
